import { useDispatch, useSelector } from 'react-redux';

import { modalOpen } from '../../../redux/dashboardActions';
import AdsInfoModal from './adsInfoModal';
import GeneralInfoModal, { GeneralInfoPair } from './generalInfoModal';
import {
  formatDimensionsRange,
  formatStringCurrency,
} from '../../../../../shared/utils';

import { Ad, TableHeader } from './types';

import { Button, Modal } from '../../../../../component';
import { IconSmallerArrow } from '../../../../../component/icon/smaller-arrow';
import { useMemo, useState } from 'react';
import { getModalInfoOffersSecondary } from '../../redux/offersSecondary.infra';
import { loaded, loading } from '../../../../../layout/redux/layoutActions';
import { updateBuildingData } from '../../redux/offersSecondary.actions';

type SortOptions = {
  field: string;
  order_by: 'asc' | 'desc';
};

const BuildingDetailsModalOffersSecondary = (): JSX.Element => {
  const dispatch = useDispatch();
  const { statusModal } = useSelector((state: any) => state.dashboardState);
  const { buildingData, realtyOffersSecondary, payloadRequest } = useSelector(
    (state: any) => state.offersSecondaryState
  );

  const { adType, realtyType } = realtyOffersSecondary;
  const [isActive, setIsActive] = useState<string>('');

  const { preference } = useSelector((state: any) => state.layoutState);
  const nls = require(`../../nls/${preference.language.id}.json`);
  const modalHead = (totalAds: string) => {
    return (
      <div className="offers-secondary-modal-head-container">
        <div className="offers-secondary-modal-head-info-container">
          <h1>{`${nls.modalOffersSecondary.header.title} (${totalAds})`}</h1>
        </div>
      </div>
    );
  };

  const generalInfoPair: GeneralInfoPair[] = [
    {
      label: nls.modalOffersSecondary.generalInfo.address,
      value: buildingData?.general_info?.address,
    },
    {
      label: nls.modalOffersSecondary.generalInfo.searchType,
      value:
        buildingData?.general_info?.search_type || adType === 'sale'
          ? 'Venda'
          : 'Locação',
    },
    {
      label: nls.modalOffersSecondary.generalInfo.propertyType,
      value: buildingData?.general_info?.property_type || realtyType?.name,
    },
    {
      label: nls.modalOffersSecondary.generalInfo.propertyDimensionsM2,
      value: buildingData?.general_info?.property_dimensions_m2
        ? formatDimensionsRange(
            buildingData?.general_info?.property_dimensions_m2
          )
        : '-',
    },
    {
      label: nls.modalOffersSecondary.generalInfo.meanPriceM2,
      value: buildingData?.general_info?.mean_price_m2
        ? formatStringCurrency(buildingData?.general_info?.mean_price_m2)
        : '-',
    },
    {
      label: nls.modalOffersSecondary.generalInfo.propertyAge,
      value: buildingData?.general_info?.property_age || '-',
    },
  ];

  const refetching = (sortOptions: SortOptions): void => {
    const address: string[] = buildingData?.general_info?.address.split(', ');
    const payload = {
      adType: payloadRequest?.adType,
      bedrooms: payloadRequest?.bedrooms,
      garages: payloadRequest?.garages,
      inputIntervals: payloadRequest?.inputIntervals,
      suites: payloadRequest?.suites,
      realtyType: payloadRequest?.realtyType,
      processed_address_street: address[0],
      processed_address_number: address[1],
      options: {
        sort: {
          field: sortOptions.field,
          order_by: sortOptions.order_by,
        },
      },
    };
    dispatch(loading());
    Promise.resolve(getModalInfoOffersSecondary(payload))
      .then((res) => {
        dispatch(updateBuildingData(res?.data?.data));
      })
      .finally(() => dispatch(loaded()));
  };

  const addingOrdenation = (
    text: string,
    oderAction: () => void
  ): JSX.Element => {
    return (
      <div className="offers-secondary-ads-column-title">
        <span>{text}</span>
        <Button
          className={`btn offers-secondary-ads-column-order-button ${
            isActive === text ? 'active' : ''
          }`}
          onClick={() => {
            setIsActive(isActive === text ? '' : text);
            oderAction();
          }}
          cy={`${text.toUpperCase()}ButtonOrder`}
        >
          <IconSmallerArrow cy={`${text.toUpperCase()}Filter`} />
        </Button>
      </div>
    );
  };

  const tableHeader: TableHeader[] = [
    {
      text: nls.modalOffersSecondary.adsInfo.address,
      column: 'address',
      className: 'left',
    },
    {
      text: addingOrdenation(nls.modalOffersSecondary.adsInfo.value, () => {
        refetching({
          field: 'price',
          order_by:
            isActive === nls.modalOffersSecondary.adsInfo.value
              ? 'asc'
              : 'desc',
        });
      }),
      column: 'value',
      className: 'left',
    },
    {
      text: addingOrdenation(
        nls.modalOffersSecondary.adsInfo.valuePerArea,
        () => {
          refetching({
            field: 'price_m2',
            order_by:
              isActive === nls.modalOffersSecondary.adsInfo.valuePerArea
                ? 'asc'
                : 'desc',
          });
        }
      ),
      column: 'value_per_area',
      className: 'left',
    },
    {
      text: addingOrdenation(nls.modalOffersSecondary.adsInfo.area, () => {
        refetching({
          field: 'area',
          order_by:
            isActive === nls.modalOffersSecondary.adsInfo.area ? 'asc' : 'desc',
        });
      }),
      column: 'area',
      className: 'text-center justify-center',
    },
    {
      text: addingOrdenation(nls.modalOffersSecondary.adsInfo.dormitory, () => {
        refetching({
          field: 'bedrooms',
          order_by:
            isActive === nls.modalOffersSecondary.adsInfo.dormitory
              ? 'asc'
              : 'desc',
        });
      }),
      column: 'dormitory',
      className: 'text-center justify-center',
    },
    {
      text: addingOrdenation(nls.modalOffersSecondary.adsInfo.suites, () => {
        refetching({
          field: 'suites',
          order_by:
            isActive === nls.modalOffersSecondary.adsInfo.suites
              ? 'asc'
              : 'desc',
        });
      }),
      column: 'suites',
      className: 'text-center justify-center',
    },
    {
      text: addingOrdenation(nls.modalOffersSecondary.adsInfo.garages, () => {
        refetching({
          field: 'garages',
          order_by:
            isActive === nls.modalOffersSecondary.adsInfo.garages
              ? 'asc'
              : 'desc',
        });
      }),
      column: 'garages',
      className: 'text-center justify-center',
    },
    {
      text: addingOrdenation(nls.modalOffersSecondary.adsInfo.adAge, () => {
        refetching({
          field: 'ad_duration',
          order_by:
            isActive === nls.modalOffersSecondary.adsInfo.adAge
              ? 'asc'
              : 'desc',
        });
      }),
      column: 'ad_age',
      className: 'text-center justify-center',
    },
    {
      text: '',
      column: 'link',
      className: 'text-center',
    },
  ];

  const tableInfo: Ad[] = useMemo(() => buildingData?.ads, [buildingData?.ads]);

  return (
    <>
      {statusModal === `open building secondary` && (
        <Modal
          cy="BuildingDetails"
          title={modalHead(buildingData?.general_info?.total_ads)}
          open={statusModal === `open building secondary`}
          close={() => dispatch(modalOpen(''))}
          size="large-full-height"
        >
          <GeneralInfoModal data={generalInfoPair} />
          <AdsInfoModal tableHeader={tableHeader} tableInfo={tableInfo} />
        </Modal>
      )}
    </>
  );
};

export default BuildingDetailsModalOffersSecondary;
